import clsx from 'clsx';
import { debounce } from 'lodash';
import moment from 'moment';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Divider from '@material-ui/core/Divider';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import Dialog from 'components/DialogWrapper/DialogWrapper';

import { useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import CalendarPlusIcon from 'mdi-material-ui/CalendarPlus';

import Button from 'components/Button/Button';
import ButtonSwitch from 'components/ButtonSwitch/ButtonSwitch';
import CountrySelect from 'components/CountrySelect/CountrySelect';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import InTime from 'components/InTime/InTime';
import TextInput from 'components/TextInput/TextInput';
import AssignmentsSelect from './AssignmentsSelect';
import useStyles from './RecordDialogLayoutStyles';

import { RECORD_STATUS } from 'LixaniAPI/enums';

import ComboBox from 'scenes/ExpenseTracking/components/ComboBox';
import sortByName from 'utils/sortByName';

import { createRecord } from '../../../../store/records/thunks/createRecord';

import { TextField } from '@material-ui/core';
import { AddUserDialog } from 'components/AddUserDialog/AddUserDialog';
import OpenAddUserDialogButton from 'components/AddUserDialog/OpenAddUserDialogButton';
import DatePickerTypeable from 'components/DatePickerTypeable/DatePickerTypeable';
import { OpenJoinEmployeeToProjectDialogButton } from 'components/JoinExistingEmployeeDialog/OpenJoinEmployeeToProjectDialogButton';
import { RESOURCES_ADD_EMPLOYEES_TO_PROJECTS } from 'scenes/Permissions/constants/permissions';
import { selectIsRequiredBusinessPermissionByBusinessId } from 'scenes/Permissions/store/selectors/personalPermissionsSelectors';
import { selectWorkCalendarEmployersArray } from 'scenes/WorkDemo/store/selectors/base-work-calendar.selectors';
import { selectProjectAssignments } from 'store/assignments/selectors/assignment.selectors';
import { loadAssignmentsForProject } from 'store/assignments/thunks/loadAssignmentsForProject';
import { selectEmployerAllowancesByBusinessId } from 'store/employerAllowances/selectors/employerAllowances.selectors';
import { selectEmployerTypeProjects, selectProjects } from 'store/projects/selectors/projectsSelectors';
import { selectCreateRecordProjectOptions } from 'store/projects/selectors/recordProjectOptions.selectors';
import { selectIsAddNewUserDialogOpen } from 'store/projectUsers/selectors/projectUsers.selectors';
import {
  closeCreateRecordDialog,
  setCreateRecordDialogAttributes,
} from 'store/records/actions/records.actions';
import {
  selectCreateRecordDialogData,
  selectIsAdminOfCreatingRecordProject,
  selectIsDialogResourcesLoading,
} from 'store/records/selectors/records.selectors';
import { selectProjectResources } from 'store/resources/selectors/resources.selectors';
import { loadResourcesAndResourceEmployersOfProject } from 'store/resources/thunks/loadResourcesAndResourceEmployersOfProject';
import { returnIsFirefoxOrSafari } from 'utils/returnIsFirefoxOrSafari.util';
import { CreateRecordTypeChange } from './createRecordTypeChange';

function CreateRecordDialog({ open }) {
  const dispatch = useDispatch();

  const dialogData = useSelector(selectCreateRecordDialogData);
  let {
    project,
    clockInAt,
    clockOutAt,
    billedClockInAt,
    billedClockOutAt,
    status,
    type,
    description,
    resource,
    assignment,
    targetCountry,
    assignmentUnitAmount,
    subType,
  } = dialogData;

  const classes = useStyles();
  const theme = useTheme();
  const upSm = useMediaQuery(theme.breakpoints.up('sm'));

  const isFirefoxOrSafari = returnIsFirefoxOrSafari();

  const { t } = useTranslation('common');

  const [unitAmountDialogOpen, setUnitAmountDialogOpen] = useState(false);

  const setAssignmentUnitAmount = (value) => {
    setAttributes({ assignmentUnitAmount: value });
  };
  const setAssignmentUnitAmountDialogOpen = (value) => {
    setUnitAmountDialogOpen(value);
  };

  const checkedOut = status === 'OUT' ? true : false;

  const projectOptions = useSelector(selectCreateRecordProjectOptions);

  const isAdminOnSelectedProject = useSelector(
    selectIsAdminOfCreatingRecordProject
  );

  const isPermissionToAddEmployees = useSelector((state) => {
    const projectBusinessId = project
      ? selectProjects(state)[project]?.business_id
      : null;
    if (!projectBusinessId) return false;
    return selectIsRequiredBusinessPermissionByBusinessId(state, {
      businessId: projectBusinessId,
      permission: RESOURCES_ADD_EMPLOYEES_TO_PROJECTS,
    });
  });

  const isResourcesLoading = useSelector(selectIsDialogResourcesLoading);
  const projectAssignments = useSelector((state) =>
    selectProjectAssignments(state, project)
  );

  const projectResources = useSelector((state) =>
    selectProjectResources(state, project)
  );

  const selectedResource = projectResources.find(
    (projectResource) => projectResource.id === resource
  );

  const workCalendarEmployers = useSelector(selectWorkCalendarEmployersArray);

  const employerProjects = useSelector(selectEmployerTypeProjects);

  const isAddNewUserDialogOpen = useSelector(selectIsAddNewUserDialogOpen);

  const employerAllowances = useSelector((state) =>
    selectEmployerAllowancesByBusinessId(
      state,
      selectedResource?.employerBusinessId || ''
    )
  );

  const handleTypeChange = (value) => {
    setAttributes({ type: value });
  };

  const handleSave = () => {
    console.log(dialogData);
    dispatch(createRecord());
  };

  const closeDialog = () => {
    dispatch(closeCreateRecordDialog());
  };

  const setAttributes = (attributeObject) => {
    dispatch(setCreateRecordDialogAttributes(attributeObject));
  };

  const [isClockOutsManuallyChanged, setIsClockOutsManuallyChanged] =
    useState(false);

  const debounceDescriptionSearch = useCallback(
    debounce(setDescriptionSearchText, 500),
    []
  );

  function setDescriptionSearchText(value) {
    console.log(value);
    setAttributes({ description: value });
  }

  const handleClockInGreaterThanClockout = (clockIn, clockOut) => {
    if (clockIn > clockOut) {
      setAttributes({ clockInAt: clockIn, clockOutAt: clockIn });
    }
  };

  const handleBilledClockInGreaterThanClockout = (clockIn, clockOut) => {
    if (clockIn > clockOut) {
      setAttributes({ billedClockInAt: clockIn, billedClockOutAt: clockIn });
    }
  };

  const handleProjectChange = (project) => {
    setAttributes({
      project: project,
      resource: undefined,
      assignment: undefined,
    });
    dispatch(loadResourcesAndResourceEmployersOfProject(project));
    dispatch(loadAssignmentsForProject(project));
  };

  const handleAssignmentChange = (assignmentId) => {
    // handling assignment deselect on click if assignment already selected
    if (assignmentId !== assignment) {
      setAttributes({
        assignment: assignmentId,
      });
    } else {
      setAttributes({
        assignment: undefined,
      });
    }
  };

  const handleClockInChange = (value) => {
    if (!isClockOutsManuallyChanged) {
      const automaticClockOutHours = moment(value).add(8.5, 'hours');

      setAttributes({
        clockInAt: formatDateOutput(value),
        clockOutAt: formatDateOutput(automaticClockOutHours),
        billedClockInAt: formatDateOutput(value),
        billedClockOutAt: formatDateOutput(automaticClockOutHours),
      });
    } else {
      setAttributes({
        clockInAt: formatDateOutput(value),
        billedClockInAt: formatDateOutput(value),
      });

      handleBilledClockInGreaterThanClockout(
        formatDateOutput(value),
        formatDateOutput(billedClockOutAt)
      );
    }
  };

  const handleClockOutChange = (value) => {
    setIsClockOutsManuallyChanged(true);

    setAttributes({
      clockOutAt: formatDateOutput(value),
      billedClockOutAt: formatDateOutput(value),
    });

    handleBilledClockInGreaterThanClockout(
      formatDateOutput(billedClockInAt),
      formatDateOutput(value)
    );
  };

  const handleBilledClockInChange = (value) => {
    setAttributes({
      billedClockInAt: formatDateOutput(value),
    });

    handleBilledClockInGreaterThanClockout(
      formatDateOutput(value),
      formatDateOutput(billedClockOutAt)
    );
  };

  const handleBilledClockOutChange = (value) => {
    setIsClockOutsManuallyChanged(true);

    setAttributes({
      billedClockOutAt: formatDateOutput(value),
    });
  };

  const clockedInGreaterThanClockedOut = clockInAt > clockOutAt;

  const dateFormat = 'YYYY-MM-DDTHH:mm';

  const formattedClockInAt = moment(clockInAt).format(dateFormat);

  const formattedClockOutAt = moment(clockOutAt).format(dateFormat);

  const formattedBilledClockInAt = moment(billedClockInAt).format(dateFormat);

  const formattedBilledClockOutAt = moment(billedClockOutAt).format(dateFormat);

  const formatDateOutput = (date) => {
    return moment(date).toISOString();
  };

  return (
    <>
      <Dialog
        aria-labelledby="create-record-dialog"
        classes={{ paper: classes.dialogPaper }}
        disableBackdropClick
        fullScreen={!upSm}
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={closeDialog}
      >
        <DialogHeader
          icon={<CalendarPlusIcon />}
          onClose={closeDialog}
          title={t('calendar.new_record')}
        />
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={2} className={classes.gridContainer}>
            <Grid item xs={12} className={classes.gridItem}>
              <Typography className={classes.inTime}>
                IN{' '}
                <InTime
                  clockInAt={moment(clockInAt)}
                  clockOutAt={moment(clockOutAt)}
                  status={checkedOut ? RECORD_STATUS.OUT : RECORD_STATUS.IN}
                />
              </Typography>
            </Grid>
            <Grid item xs={12} className={classes.clockedItem}>
              <ButtonSwitch
                checked={!checkedOut}
                fullWidth
                on={t('record.clocked_in')}
                off={t('record.clocked_out')}
                onChange={() =>
                  setAttributes({ status: checkedOut ? 'IN' : 'OUT' })
                }
              />
            </Grid>

            {/*LEIMATTU AIKA*/}
            <Grid item xs={12}>
              <label className="mylabel">{t('calendar.clocked_time')}</label>
            </Grid>
            <Grid item xs={6}>
              {isFirefoxOrSafari ? (
                <DatePickerTypeable
                  label={t('calendar.start_date')}
                  iconVisible
                  showTimeSelect
                  timeIntervals={5}
                  dateFormat="dd.MM.yyyy HH:mm"
                  value={formattedClockInAt}
                  onChange={(date) => {
                    handleClockInChange(date);
                  }}
                />
              ) : (
                <TextField
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ className: classes.textInput }}
                  fullWidth
                  shrink={true}
                  label={t('calendar.start_date')}
                  variant="outlined"
                  size="small"
                  type="datetime-local"
                  value={formattedClockInAt}
                  onChange={(event) => handleClockInChange(event.target.value)}
                  onBlur={(event) =>
                    handleClockInGreaterThanClockout(
                      formatDateOutput(event.target.value),
                      formatDateOutput(clockOutAt)
                    )
                  }
                />
              )}
            </Grid>
            <Grid
              item
              xs={6}
              className={clsx(classes.dateItem, checkedOut && classes.hidden)}
            >
              <Typography className={classes.clockedText}>
                {t('calendar.clocked_in')}
              </Typography>
            </Grid>
            <Grid
              item
              xs={6}
              className={clsx(classes.dateItem, !checkedOut && classes.hidden)}
            >
              {isFirefoxOrSafari ? (
                <DatePickerTypeable
                  label={t('calendar.end_date')}
                  iconVisible
                  showTimeSelect
                  timeIntervals={5}
                  dateFormat="dd.MM.yyyy HH:mm"
                  value={formattedClockOutAt}
                  minDate={new Date(formattedClockInAt)}
                  onChange={(date) => {
                    handleClockOutChange(date);
                  }}
                />
              ) : (
                <TextField
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ className: classes.textInput }}
                  fullWidth
                  shrink={true}
                  label={t('calendar.end_date')}
                  variant="outlined"
                  size="small"
                  type="datetime-local"
                  value={formattedClockOutAt}
                  onChange={(event) => handleClockOutChange(event.target.value)}
                  onBlur={(event) =>
                    handleClockInGreaterThanClockout(
                      formatDateOutput(clockInAt),
                      formatDateOutput(event.target.value)
                    )
                  }
                />
              )}
            </Grid>
            {/*LASKUTETTAVA AIKA*/}
            {isAdminOnSelectedProject ? (
              <>
                <Grid item xs={12}>
                  <label className="mylabel">{t('calendar.billed_time')}</label>
                </Grid>
                <Grid item xs={6}>
                  {isFirefoxOrSafari ? (
                    <DatePickerTypeable
                      label={t('calendar.start_date')}
                      iconVisible
                      showTimeSelect
                      timeIntervals={5}
                      dateFormat="dd.MM.yyyy HH:mm"
                      value={formattedBilledClockInAt}
                      onChange={(date) => {
                        handleBilledClockInChange(date);
                      }}
                    />
                  ) : (
                    <TextField
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ className: classes.textInput }}
                      fullWidth
                      shrink={true}
                      label={t('calendar.start_date')}
                      variant="outlined"
                      size="small"
                      type="datetime-local"
                      value={formattedBilledClockInAt}
                      onChange={(event) =>
                        handleBilledClockInChange(event.target.value)
                      }
                    />
                  )}
                </Grid>
                <Grid
                  item
                  xs={6}
                  className={clsx(
                    classes.dateItem,
                    checkedOut && classes.hidden
                  )}
                >
                  <Typography className={classes.clockedText}>
                    {t('calendar.clocked_in')}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={6}
                  className={clsx(!checkedOut && classes.hidden)}
                >
                  {isFirefoxOrSafari ? (
                    <DatePickerTypeable
                      label={t('calendar.end_date')}
                      iconVisible
                      showTimeSelect
                      timeIntervals={5}
                      dateFormat="dd.MM.yyyy HH:mm"
                      value={formattedBilledClockOutAt}
                      onChange={(date) => {
                        handleBilledClockOutChange(date);
                      }}
                      minDate={new Date(formattedBilledClockInAt)}
                    />
                  ) : (
                    <TextField
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ className: classes.textInput }}
                      fullWidth
                      shrink={true}
                      label={t('calendar.end_date')}
                      variant="outlined"
                      size="small"
                      type="datetime-local"
                      value={formattedBilledClockOutAt}
                      onChange={(event) =>
                        handleBilledClockOutChange(event.target.value)
                      }
                      onBlur={(event) =>
                        handleBilledClockInGreaterThanClockout(
                          formatDateOutput(billedClockInAt),
                          formatDateOutput(event.target.value)
                        )
                      }
                    />
                  )}
                </Grid>
              </>
            ) : null}

            <Grid item xs={12} className={classes.gridItem}>
              <ComboBox
                items={projectOptions}
                selected={project}
                onChange={(e) => handleProjectChange(e)}
                label={t('project.project')}
                emptyOption={false}
              />
            </Grid>

            <Grid item xs={12} className={classes.gridItem}>
              <CreateRecordTypeChange
                handleChange={handleTypeChange}
                selectedType={type}
                subType={subType}
                employerAllowances={employerAllowances}
              />
            </Grid>

            {type === 'RIDE' && (
              <Grid item xs={12} className={classes.gridItem}>
                <CountrySelect
                  changeCountry={(country) =>
                    setAttributes({ targetCountry: country })
                  }
                  label={t('record.country')}
                  propsCountry={targetCountry}
                  fullWidth
                />
              </Grid>
            )}

            <Grid item xs={12} sm={7} className={classes.gridItem}>
              <ComboBox
                items={projectResources
                  .sort((a, b) => sortByName(a, b, 'lastName'))
                  .map((resource) => {
                    const resourceEmployer = [
                      ...workCalendarEmployers,
                      ...employerProjects,
                    ].find(
                      (employerProject) =>
                        employerProject.business_id ===
                        resource.employerBusinessId
                    );

                    if (resourceEmployer && resourceEmployer.name) {
                      return {
                        ...resource,
                        name: `${resourceEmployer.name} / ${resource.lastName} ${resource.firstName}`,
                      };
                    } else {
                      return {
                        ...resource,
                        name: `MyLixani (Ei työnantajaa) / ${resource.lastName} ${resource.firstName}`,
                      };
                    }
                  })}
                selected={resource}
                onChange={(value) => setAttributes({ resource: value })}
                label={t('common.person')}
                emptyOption={false}
                disabled={isResourcesLoading}
                loading={isResourcesLoading}
                error={!resource}
              />
            </Grid>
            {isPermissionToAddEmployees ? (
              <Grid
                item
                xs={12}
                sm={5}
                className={classes.gridItem}
                style={{ display: 'flex', gap: '5px' }}
              >
                <OpenJoinEmployeeToProjectDialogButton
                  projectId={project}
                ></OpenJoinEmployeeToProjectDialogButton>
                <OpenAddUserDialogButton />
              </Grid>
            ) : null}

            <Grid item xs={12} className={classes.gridItem}>
              <TextInput
                fullWidth
                id="description"
                onChange={(e) => debounceDescriptionSearch(e.target.value)}
                label={t('calendar.description')}
                multiline
              />
            </Grid>

            <Grid item xs={12} className={classes.gridItem}>
              <FormControl fullWidth>
                <AssignmentsSelect
                  a1={
                    projectAssignments && projectAssignments[0]
                      ? projectAssignments[0]
                      : undefined
                  }
                  selectedAssignment={assignment}
                  assignments={projectAssignments}
                  update={handleAssignmentChange}
                  unitAmountDialogOpen={unitAmountDialogOpen}
                  setDialogOpen={setAssignmentUnitAmountDialogOpen}
                  setAssignmentUnitAmount={setAssignmentUnitAmount}
                  unitAmount={assignmentUnitAmount}
                />
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions className={classes.dialogActions}>
          <Button
            className={classes.actionBtn}
            onClick={closeDialog}
            outline
            color="primary"
          >
            {t('common.cancel')}
          </Button>
          <Button
            className={classes.actionBtn}
            onClick={handleSave}
            color="primary"
            disabled={clockedInGreaterThanClockedOut || !resource}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
      {isAddNewUserDialogOpen ? <AddUserDialog /> : null}
    </>
  );
}

export default CreateRecordDialog;
